import { PRODUCT_UPSELL_TYPE, parseHandleList, type BlockInstance, type CartSettings } from "@dk/theme"

/** The `product-upsell` strip: seller-picked products the shopper switches on from
 *  the cart drawer, or ticks on the product page.
 *
 *  This module is the merge of what used to be two: `cartupsell.ts` (the `upsells`
 *  block) and `cartaddons.ts` (the `add-ons` block, whose products came from a
 *  store-wide `cart/addons` settings row). One block, one resolver, one ledger —
 *  see @dk/theme's product-upsell.ts for why, and for the block migration.
 *
 *  Pure and DOM-free on purpose (storage is injected) so it is unit-testable in the
 *  storefront's node vitest environment.
 */

/** How many offers the strip shows at most — Shrine's is three rows, and the
 *  drawer has to leave room for the checkout button. */
export const MAX_PRODUCT_UPSELLS = 3

export interface ProductUpsellConfig {
  /** Whether the strip is on at all (no fetch when false). */
  enabled: boolean
  /** Handles to offer, already capped and de-duplicated. */
  handles: string[]
  /** The seller pre-selects the offers for a shopper who has decided nothing. In
   *  the drawer that puts them IN the cart; on the PDP it ticks them, and they go
   *  in with the main product. Off by default and warned about in the builder:
   *  pre-ticking a paid extra is banned outright by the EU Consumer Rights
   *  Directive art. 22 and the UK CCRs 2013 reg. 40. */
  defaultOn: boolean
}

/**
 * Resolve the strip from the `product-upsell` block, with the Cart theme settings
 * as fallback — the block wins, blank falls back.
 *
 * The fallback is why `upsell_enabled` / `upsell_handles` still exist: a store that
 * configured its recommendations there, before the block system, keeps its strip
 * without anything having to rewrite its theme document.
 *
 * The block's own picks also ENABLE the strip. `upsell_enabled` lives on a
 * different page of the builder (Theme settings → Cart), so gating a block's
 * products behind it means a seller adds the block, picks products, and sees
 * nothing — with no indication of where the missing switch is. Choosing products on
 * a block that exists is the intent.
 *
 * `blocks` is expected to be post-migration (`mergeProductUpsellBlocks`), which is
 * what the block host hands it; a legacy `upsells` / `add-ons` type is therefore
 * not looked for here.
 */
export function resolveProductUpsell(blocks: BlockInstance[], cartSettings: CartSettings): ProductUpsellConfig {
  const block = blocks.find((b) => b.type === PRODUCT_UPSELL_TYPE)
  if (!block) return { enabled: false, handles: [], defaultOn: false }
  return resolveFromSettings(block.settings as Record<string, unknown> | undefined, cartSettings)
}

/** The same resolution for a host that already holds the block's settings — the PDP
 *  block, which is handed its own `settings` by the block loop.
 *
 *  `fallback` is omitted there deliberately: `upsell_handles` is a CART setting, so
 *  letting it feed the product page would materialise a strip on every PDP of a
 *  store that had only ever configured its cart. */
export function resolveFromSettings(
  settings: Record<string, unknown> | undefined,
  fallback?: CartSettings,
): ProductUpsellConfig {
  const own = parseHandleList(settings?.handles)
  const inherited = fallback ? parseHandleList(fallback.upsell_handles) : []
  const handles = own.length ? own : inherited
  return {
    enabled: own.length > 0 || fallback?.upsell_enabled === true,
    handles: handles.slice(0, MAX_PRODUCT_UPSELLS),
    // Block-only: there is no Cart theme setting for it, and a pre-selection is a
    // charge, so it may only come from a control the seller actually saw.
    defaultOn: settings?.default_on === true,
  }
}

// ---- the decision ledger ---------------------------------------------------
//
// WHY THE DEFAULT IS APPLIED IN THE BROWSER
// -----------------------------------------
// `default_on` could only be honoured server-side by adding a line during a cart
// READ, and a GET that mutates the cart is wrong for the ordinary reasons — it
// fires on every prefetch, every bot, every revalidation. So the seller's flag
// ships with the strip and the client acts on it once.
//
// WHY A LEDGER IS NEEDED AT ALL
// -----------------------------
// Without one, "pre-selected" would mean "un-removable": the shopper switches an
// offer off, the drawer re-renders, the rule fires again and puts it back.
// Recording the decision is what makes off actually mean off. Only an EXPLICIT
// toggle is recorded — an offer the shopper never touched keeps no entry, so a
// seller enabling `default_on` later still reaches them.

export type Choice = "on" | "off"
export type Choices = Record<string, Choice>

/** Keyed by CART, not by browser: a recovery link or a fresh cart is a new
 *  decision, and a stale ledger would otherwise suppress the seller's defaults
 *  forever.
 *
 *  The `:addons:` in the key is historical and deliberately kept. Renaming it would
 *  discard the ledger of every cart open at the moment this deploys — and the entry
 *  that gets discarded is the one recording "the shopper switched this OFF", so a
 *  pre-selected offer they had refused would come straight back. */
export function choicesKey(cartToken: string): string {
  return `dk:cart:addons:${cartToken}`
}

export interface ChoiceStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export function readChoices(store: ChoiceStore, cartToken: string): Choices {
  if (!cartToken) return {}
  try {
    const raw = store.getItem(choicesKey(cartToken))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return {}
    const out: Choices = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (v === "on" || v === "off") out[k] = v
    }
    return out
  } catch {
    return {} // unreadable or full storage — the shopper just gets the defaults again
  }
}

export function recordChoice(store: ChoiceStore, cartToken: string, key: string, on: boolean): Choices {
  const next = { ...readChoices(store, cartToken), [key]: on ? "on" : ("off" as Choice) } as Choices
  if (!cartToken) return next
  try {
    store.setItem(choicesKey(cartToken), JSON.stringify(next))
  } catch {
    /* the toggle still worked for this page load */
  }
  return next
}

/** The ledger entry for one offer.
 *
 *  Keyed by HANDLE rather than by variant id: the handle is what the seller picked,
 *  so re-picking the offered variant (a sold-out colour, say) must not read as a
 *  fresh offer and re-add something the shopper already refused. */
export function upsellChoiceKey(handle: string): string {
  return `upsell:${handle}`
}

/** Minimum an offer has to expose to be judged — kept structural so the rule can be
 *  unit-tested without the storefront's Vue-side `UpsellItem`. */
export interface UpsellOffer {
  product_handle: string
  /** true when the cart already holds it */
  selected: boolean
}

/** The offers a `default_on` strip should switch on right now: not already in the
 *  cart, and never explicitly decided by this shopper. */
export function pendingUpsellDefaults<T extends UpsellOffer>(offers: readonly T[], choices: Choices): T[] {
  return offers.filter((o) => !o.selected && choices[upsellChoiceKey(o.product_handle)] === undefined)
}
